<template>
    <div class="text-value text-profile widget-height-inner scroll-auto">
        <loading-message v-if="loading"></loading-message>
        <VisualOnlineProfile v-if="chartType === 'lines'" :visualData="visualData" :metadata="chartMetadata"/>
        <TextOnlineProfileMetric v-if="chartType === 'text'" :profileMetrics="visualData" :metadata="chartMetadata"/>
    </div>
</template>

<script>
import VuexStore from "@/store/vuex/VuexStore";
import OnlineProfileMetricSettingsDialog from "@/dashboards/widgets/onlineprofile/OnlineProfileMetricSettingsDialog.vue";
import VisualOnlineProfile from "@/dashboards/widgets/onlineprofile/VisualOnlineProfile.vue";
import TextOnlineProfileMetric from "@/dashboards/widgets/onlineprofile/textonlineprofile/TextOnlineProfileMetric.vue";
import {mapState} from "vuex";
import {findAllNodes, parseFilterString} from "@/dashboards/filter/FilterParser";
import {AndNode, MentionQLexer} from "@/mentionq/mentionq";
import _ from "underscore";
import {beansprout} from "@/store/Services";
import {OnlineProfileStats} from "@/app/utils/types";
import moment from 'moment';
import LoadingMessage from "@/components/LoadingMessage.vue";

if (module.hot) window.beWidgetReloaded("OnlineProfileMetric");

export default {
    name: 'OnlineProfileMetric',
    components: {LoadingMessage, TextOnlineProfileMetric, VisualOnlineProfile},
    store: VuexStore,

    widgetType: {
        description: "See likes, followers, and so on, for Online Profiles from various Social Networks",
        group: "benchmarking",
        width: 4,
        height: 4,
        vueSettingsDialog: OnlineProfileMetricSettingsDialog
    },

    props: {
        model: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            loading: false,
            message: null,
            accountCode: null,
            filter: this.model.get('_effectiveFilter'),
            profilesToDisplay: [],

            /**
             * @type {OnlineProfileStats[]} with additional props
             */
            visualData: [],

            chartMetadata: {
                fontSize: 14,
                numberFormat: '9200',
                dateFormat: 'Auto',
                groupBy: 'Day'
            }
        }
    },

    computed: {
        ...mapState(['rootBrands']),
        selectedProfiles() {
            return this.model.attributes.profiles || [];
        },
        chartType() {
            console.log('changing chart-type: ', this.model.attributes['chart-type'])
            return this.model.attributes['chart-type'] || 'lines';
        },

        fontSize() {
            return this.model.attributes['font-size'] || 14;
        },

        numberFormat() {
            return this.model.attributes['number-format'] || '9 200';
        },

        dateFormat() {
            return this.model.attributes['date-format'] || 'Auto';
        },

        groupBy() {
            return this.model.attributes['group-by'] || 'Day';
        },

        effectiveFilter() {
            return this.model.attributes._effectiveFilter;
        }
    },

    async mounted() {
        this.accountCode = this.model.getAncestorProperty('accountCode');
        await VuexStore.dispatch("competitorProfiles/refreshCompetitorProfiles");

        // main filter with filterLang date range
        this.filter = this.model.get('_effectiveFilter')
        this.refresh()
    },

    methods: {
        refresh() {
            console.log('OnlineProfileMetric refresh called');

            // this is the data from the setting dialogue
            const profiles = this.model.attributes.profiles;
            const profilesForRequest = profiles.map((profile) => {
                return {profileId: profile.id, profileHandleId: profile.handleId}
            });

            const date = this.getDateFromFilter();

            this.chartMetadata.fontSize = this.fontSize;
            this.chartMetadata.numberFormat = this.numberFormat;
            this.chartMetadata.dateFormat = this.dateFormat;
            this.chartMetadata.groupBy = this.groupBy;
            this.getProfileTimelineData(profilesForRequest, date, this.groupBy);
        },

        getDateFromFilter() {
            const publishedNodes = findAllNodes(parseFilterString(this.filter), function (node) {
                return node.operandType === MentionQLexer.PUBLISHED;
            });

            const dateFilter = _(publishedNodes).reduce(function (lhs, rhs) {
                return new AndNode(lhs, rhs);
            }).toString();

            return dateFilter;
        },

        async getProfileTimelineData(profileData, dateFilter, grouping = 'daily') {
            if (profileData.length === 0) {
                this.visualData = [];
                return;
            }

            this.loading = true;
            const res = await beansprout.post('/rest/profile-stats-timeline',
                {profiles: profileData, filter: dateFilter, groupBy: grouping});

            if (res.status !== 200) {
                console.warn("An error occurred while fetching timeline metrics. Please try again later or contact support if the problem persists.")
            }

            if (Object.keys(res.data).length !== 0) {

                const graphData = [];

                for (let dataKey in res.data) {
                    res.data[dataKey] = this.fillMissingDates(res.data[dataKey]);

                    const firstStat = res.data[dataKey][0];
                    res.data[dataKey].map(profileStats => {
                        profileStats.captured = profileStats.statDay;
                        profileStats.date = profileStats.statDay;

                        // calculate the deltas over the selected period, firstStat is fromDate
                        // /profile-stats-timeline endpoint orders data in ascending

                        // Calculate likes delta only if both values exist (Facebook and TikTok)
                        if (profileStats.likeCount != null && firstStat.likeCount != null) {
                            profileStats.likesDelta = profileStats.likeCount - firstStat.likeCount;
                        }

                        // Calculate follower count delta (Facebook, Instagram, TikTok, LinkedIn, YouTube, Twitter)
                        if (profileStats.followerCount != null && firstStat.followerCount != null) {
                            profileStats.followerCountDelta = profileStats.followerCount - firstStat.followerCount;
                        }

                        // YouTube-specific metrics
                        if (profileStats.channel && profileStats.channel.toLowerCase().includes('youtube')) {
                            if (profileStats.metrics && firstStat.metrics &&
                                profileStats.metrics.video_count != null && firstStat.metrics.video_count != null) {
                                profileStats.videoCount = profileStats.metrics.video_count;
                                profileStats.statusDelta = profileStats.metrics.video_count - firstStat.metrics.video_count;
                            }
                        }

                        // Twitter-specific metrics
                        if (profileStats.channel && profileStats.channel.toLowerCase().includes('twitter')) {
                            if (profileStats.metrics && firstStat.metrics) {
                                // Post count (tweets)
                                if (profileStats.metrics.post_count != null && firstStat.metrics.post_count != null) {
                                    profileStats.postCount = profileStats.metrics.post_count;
                                    profileStats.postDelta = profileStats.metrics.post_count - firstStat.metrics.post_count;
                                }

                                // Following count
                                if (profileStats.metrics.following_count != null && firstStat.metrics.following_count != null) {
                                    profileStats.followingCount = profileStats.metrics.following_count;
                                    profileStats.followingCountDelta = profileStats.metrics.following_count - firstStat.metrics.following_count;
                                }
                            }
                        }

                        profileStats.isBeansproutMetrics = true;

                        // Ensure we have the profile information for tooltip
                        profileStats.profile = {
                            handle: profileStats.name,
                            name: profileStats.name,
                            type: profileStats.channel
                        };
                    });

                    const firstProfileStat = res.data[dataKey][0];
                    graphData.push({
                        key: firstProfileStat.name,
                        lcLey: firstProfileStat.name.toLowerCase(),
                        values: res.data[dataKey],
                        isBeansproutMetrics: true
                    });
                }

                this.visualData = graphData;
                this.loading = false;
            }
        },

        /**
         * Fills in missing dates in profile stats array with the previous day's values
         */
        fillMissingDates(profileStats) {
            if (!profileStats || profileStats.length === 0) {
                return profileStats;
            }

            // Parse dates and sort to ensure ascending order
            const sortedStats = profileStats.map(stat => ({
                ...stat,
                parsedDate: moment(stat.statDay)
            })).sort((a, b) => a.parsedDate - b.parsedDate);

            const filledStats = [];
            const firstDate = sortedStats[0].parsedDate;
            const lastDate = sortedStats[sortedStats.length - 1].parsedDate;

            // Iterate through each day in the range
            let currentDate = firstDate.clone();
            let statsIndex = 0;

            while (currentDate.isSameOrBefore(lastDate, 'day')) {
                const currentDateStr = currentDate.format('YYYY-MM-DD');
                const existingStat = sortedStats[statsIndex];
                const existingDateStr = existingStat.parsedDate.format('YYYY-MM-DD');

                if (currentDateStr === existingDateStr) {
                    // date already exists in data
                    filledStats.push(existingStat);
                    statsIndex++;
                } else {
                    // a date is missing, create a new entry with the previous day's values
                    const previousStat = filledStats[filledStats.length - 1];
                    if (previousStat) {
                        const filledStat = {
                            ...previousStat,
                            statDay: currentDateStr,
                            parsedDate: currentDate.clone()
                        };
                        filledStats.push(filledStat);
                    }
                }

                currentDate.add(1, 'day');
            }

            // Remove the temporary parsedDate property
            return filledStats.map(stat => {
                const {parsedDate, ...rest} = stat;
                return rest;
            });
        },
    },

    watch: {
        selectedProfiles: {
            handler: function(newVal, oldVal) {
                this.refresh();
            },
            deep: true
        },
        chartType(newVal, oldVal) {
            this.refresh();
        },

        effectiveFilter() {
            this.filter = this.model.get('_effectiveFilter');
            this.refresh();
        }
    }
}
</script>

<style scoped lang="sass">
.text-value
    padding: 20px
    display: flex
    flex-direction: column
    height: 100%
    box-sizing: border-box

.loading-message, .error-message
    padding: 20px
    text-align: center
</style>
